import type { MailboxProvider } from '@immo/api-contract';
import { EmailComposer } from './email-composer';
import type { ComposerDescriptor } from './use-composers';

type ComposerDockProps = {
  readonly composers: ReadonlyArray<ComposerDescriptor>;
  readonly workspaceId: string;
  readonly connectedEmail: string | null;
  // The connected mailbox's provider — badges the sender in the composer preview.
  readonly provider?: MailboxProvider | null;
  readonly onToggleMinimise: (id: string) => void;
  readonly onClose: (id: string) => void;
  readonly onSent: (id: string) => void;
  // Minimised bars already docked by another host; this dock's bars sit after them.
  readonly slotBase?: number;
};

// Renders each composer via the system Modal when open, and as a small docked bar
// at the bottom-right when minimised (Gmail-style) — both self-portal to the body,
// so `EmailComposer` is mapped directly by a stable key. Keeping the same keyed
// element across a minimise toggle preserves each composer's typed content (the
// state lives in `EmailComposer`, not in the modal/bar it renders). Multiple
// composers may be open at once, though in practice one is focused.
export function ComposerDock({
  composers,
  workspaceId,
  connectedEmail,
  provider,
  onToggleMinimise,
  onClose,
  onSent,
  slotBase = 0,
}: ComposerDockProps) {
  if (composers.length === 0) return null;

  // Give each minimised bar its own slot so they sit side by side, not stacked.
  const minimisedSlot = new Map<string, number>();
  composers.forEach((composer) => {
    if (composer.minimised) minimisedSlot.set(composer.id, minimisedSlot.size);
  });

  return (
    <>
      {composers.map((composer) => (
        <EmailComposer
          key={composer.id}
          workspaceId={workspaceId}
          mode={composer.mode}
          seed={composer.seed}
          connectedEmail={connectedEmail}
          provider={provider ?? null}
          minimised={composer.minimised}
          dockOffset={slotBase + (minimisedSlot.get(composer.id) ?? 0)}
          onMinimiseToggle={() => onToggleMinimise(composer.id)}
          onClose={() => onClose(composer.id)}
          onSent={() => onSent(composer.id)}
        />
      ))}
    </>
  );
}
